import type { H3Event } from 'h3'
import type { AdoptOutcome } from '~/composables/useUrlCoupon'
import { codesDisplacedBy, couponWriteLock } from '~/composables/useUrlCoupon'

/**
 * Prize codes already taken off a URL in this page life. Only the browser half
 * below touches it, so this module-level set is never shared between two server
 * renders.
 */
const handledPrizeCodes = new Set<string>()

/**
 * Carries a code from a `?code=` URL through to the cart — a campaign coupon, or
 * a personal scratch-card prize code.
 *
 * Two jobs: validate whatever code the URL carries (see `useUrlCoupon`), and
 * put the accepted code on the order as soon as the order has something to
 * apply it to. Applying it here rather than at checkout is what makes the cart
 * drawer, the totals and the checkout summary all show the campaign price — or
 * hold the gift — without any of them knowing about the campaign.
 *
 * The validation half runs on the SERVER too, so the first HTML a campaign
 * visitor is sent already prices every product with the code deducted — the
 * tiles, the PDP and the JSON-LD alike — instead of painting the catalogue
 * price and correcting it once the bundle boots. The validated coupon travels
 * to the browser in the payload (`useState`), so hydration re-uses it rather
 * than repeating the round trip. The cart half stays client-only: the active
 * order is only ever fetched in the browser.
 *
 * A server render for a visitor who arrived holding the `promo.code` cookie is
 * marked `no-store`: the cookie is not in the CDN's cache key, and
 * `/{locale}/products/**` is otherwise edge-cached for an hour (see
 * `HTML_CACHE_CONTROL` in `nuxt.config`), which would hand one shopper's
 * discount to everyone behind the same CDN object. A `?code=` landing whose
 * code validated is not: the code is in the URL, so the discounted HTML is
 * keyed to the very link that promised it — see `keepThisRenderPrivate`.
 *
 * A cart that already carries a code is not simply left alone. The code the link
 * carries is the one the customer was just promised — and, for an affiliate
 * link, the one whose redemption is attributed — so it takes the place of
 * whatever it cannot sit beside: a code of its own kind, whatever that code was
 * worth. A code of the other kind stays next to it (an order holds one
 * fixed-amount and one percentage code) — see `codesDisplacedBy`. The one
 * exception is a prize code already on the order, which keeps its slot.
 *
 * Prize codes differ in four ways, and all four are deliberate:
 * - they live in their own carrier (cookie `prize.code`), so a later campaign
 *   link cannot overwrite a gift the customer has been e-mailed;
 * - they are recognised SYNCHRONOUSLY, by prefix, because a personal code must
 *   be off the address bar before anything can read it;
 * - they never remove another live code silently — the cart drawer's banner
 *   names the blocker and offers a one-tap swap (`useUrlPrize`);
 * - and they are adopted in the BROWSER only: confirming one is a mutation, and
 *   a page render must not send one.
 *
 * Loads after `2.activeOrder`, whose `$activeOrder` the sync below feeds, and
 * declares `graphql-config` as a dependency: that plugin is what puts the
 * channel token, locale and currency on every GraphQL call, and the server half
 * below makes one during plugin execution rather than from a component.
 */
export default defineNuxtPlugin({
  name: 'url-coupon',
  dependsOn: ['graphql-config'],
  async setup() {
    await setupUrlCoupon()
  },
})

async function setupUrlCoupon() {
  const { coupon, storedCode, adopt, restore, forget } = useUrlCoupon()

  if (import.meta.server) {
    // `useRoute()` inside a plugin is not yet the resolved route, and the query
    // is all this half needs, so the request URL is read directly. This, the
    // event and the incoming cookie are all read BEFORE the round trip below:
    // awaiting inside a plugin loses the Nuxt instance, and every composable
    // that needs it with it.
    const code = useRequestURL().searchParams.get('code')
    const event = useRequestEvent()
    // Whether the visitor ARRIVED with a code, read before `adopt` can overwrite
    // it. A render that only retires a dead code is personal too: it carries the
    // `Set-Cookie` that clears it.
    const carriedCode = Boolean(storedCode.value)
    // A visitor with neither takes no round trip at all: `restore()` returns
    // immediately when there is no stored code.
    //
    // A prize code is deliberately left alone here, in the URL and in the
    // cookie: adopting one confirms the mailbox behind it, which is a mutation,
    // and it has to come off the address bar before anything can read it —
    // both jobs for the browser half below. Nothing personal therefore reaches
    // this HTML, so a prize link needs no cache treatment of its own either.
    const campaignCode = code && !isPrizeCode(code) ? code : null
    await (campaignCode ? adopt(campaignCode) : restore({ campaignOnly: true }))
      .catch(error => console.error('Coupon validation failed', error))

    // A `?code=` that validated stays shareable: `adopt` wrote the prices and
    // the `Set-Cookie`, and both are the same for everyone who follows that
    // link, so the edge may keep the page under the link's own key. One that
    // did NOT validate is kept private even though it looks like the plain
    // page: `couponPreview` runs under this visitor's session, and a signed-in
    // customer who has used up their own allowance of a live code is refused
    // where everyone else is accepted — cached at the campaign's URL, their
    // plain render would greet every later visitor with the catalogue price. A
    // failed round trip ends up here too, which is the right side to err on.
    if (carriedCode || (campaignCode && !coupon.value))
      keepThisRenderPrivate(event)

    // Everything below is about the cart, which does not exist server-side.
    return
  }

  const route = useRoute()
  const router = useRouter()
  const urlPrize = useUrlPrize()
  const activeOrderStore = useActiveOrderStore()
  const { syncLegacyActiveOrder } = useCartAdd()
  const drawers = useDrawers()

  function firstQueryValue(value: unknown): string | null {
    const first = Array.isArray(value) ? value[0] : value
    return typeof first === 'string' ? first : null
  }

  // ---- The prize code, off the address bar before anything can read it ------
  //
  // A personal code must not reach analytics, session replay, shares or the
  // Referer. GTM loads later (nuxt-delay-js) and Faro initialises from a dynamic
  // import (layers/common/app/plugins/0.telemetry.client.ts), so both read a
  // clean location — provided this runs before any await.

  const initialCode = firstQueryValue(route.query.code)
  if (initialCode && isPrizeCode(initialCode)) {
    stripCodeFromAddressBar()
    takePrizeCode(initialCode)
  }

  // Every navigation too, including Nuxt's own initial client replace at
  // `app:created` — whose afterEach feeds GTM's route_change pagePath — and
  // pages that rebuild the query from `route.query`.
  router.beforeEach((to) => {
    const code = firstQueryValue(to.query.code)
    if (!code || !isPrizeCode(code))
      return
    takePrizeCode(code)
    const { code: _code, ...query } = to.query
    return { path: to.path, query, hash: to.hash, replace: true }
  })

  function stripCodeFromAddressBar(): void {
    try {
      const url = new URL(window.location.href)
      if (!url.searchParams.has('code'))
        return
      url.searchParams.delete('code')
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
    }
    catch {
      // A location we cannot parse is a location we cannot rewrite; the code is
      // still adopted below.
    }
  }

  /** Validated once per code per page life; a failed round trip may try again. */
  function takePrizeCode(raw: string): void {
    const code = raw.trim().toUpperCase()
    if (!code || handledPrizeCodes.has(code))
      return
    handledPrizeCodes.add(code)
    adopt(code).then(onUrlOutcome).catch((error) => {
      handledPrizeCodes.delete(code)
      console.error('Prize code validation failed', error)
    })
  }

  function onUrlOutcome(outcome: AdoptOutcome): void {
    if (outcome.kind !== 'prize')
      return
    if (outcome.status === 'LIVE')
      void revealPrize()
    else
      urlPrize.announce(outcome.status)
  }

  /**
   * The e-mail link was opened: once the cart is known and nothing else is
   * writing to it, show the customer where their gift stands.
   */
  async function revealPrize(): Promise<void> {
    await waitFor(() => activeOrderStore.activeOrder.data !== undefined && !couponWriteLock.busy, 15_000)
    if (route.path.includes('/checkout') || drawers.value.cart.open)
      return
    if (document.getElementById('cart-drawer-panel')) {
      // The drawer's banner says 'waiting' or 'blocked', or the gift dialog opens.
      drawers.value.cart.open = true
      return
    }
    const codes = (activeOrderStore.activeOrder.data?.couponCodes ?? []).map(code => code.toUpperCase())
    if (!codes.includes(urlPrize.prize.value?.code ?? ''))
      urlPrize.announce('WAITING')
  }

  // ---- Campaign codes ------------------------------------------------------

  // A `?code=` page that validated is shared at the edge (see
  // `keepThisRenderPrivate`), and whether the copy served from there still
  // carries the `Set-Cookie` the origin wrote beside it is the CDN's business,
  // not something a page may rely on. The code itself travels safely in the
  // payload, so the carrier is brought in line from that before anything
  // below reads it — `adopt` recognises a held code without touching the
  // cookie, and without the cookie the coupon would not survive the next full
  // page load.
  if (coupon.value && storedCode.value !== coupon.value.code)
    storedCode.value = coupon.value.code

  watch(() => route.query.code, (value) => {
    const code = firstQueryValue(value)
    // No campaign code in this URL: a code adopted earlier in the journey still
    // stands. A failed validation round trip is not a verdict on the code —
    // swallow it and leave whatever state we had; the next navigation retries.
    // On the first run the campaign half is usually a no-op: the server already
    // validated whatever this URL carried, and both `adopt` and `restore`
    // recognise a coupon that is already held rather than asking again. The
    // prize half is not — the server deliberately left it to us.
    ;(code && !isPrizeCode(code) ? adopt(code) : restore())
      .catch(error => console.error('Coupon validation failed', error))
  }, { immediate: true })

  watch([() => activeOrderStore.activeOrder.data, coupon, urlPrize.prize], async ([order, urlCoupon]) => {
    if (couponWriteLock.busy)
      return
    // The prize goes first: it is the code the customer was personally given,
    // and it decides whether this order state has already been answered.
    if (await urlPrize.tryApply(order) === 'handled')
      return
    if (!urlCoupon || !order?.lines?.length)
      return
    // Only touch a cart that is being built. The store's applyCouponCode first
    // transitions the order to AddingItems, so firing against an order sitting
    // in ArrangingPayment (a payment confirming in another tab, a redirect
    // return before its rollback) would yank it out mid-payment.
    if (order.state !== 'AddingItems')
      return

    const existing = (order.couponCodes ?? []).map(code => code.toUpperCase())
    // Our own code is already on the order. This is also what stops the watcher
    // from reacting to its own result, and a code the customer deliberately
    // removed does not come back either: the removal handler drops the campaign
    // coupon (see `removePromo` in checkout).
    if (existing.includes(urlCoupon.code))
      return
    // A prize code holds the one code slot: stop advertising a campaign price
    // this cart will not give.
    if (existing.some(code => isPrizeCode(code))) {
      forget()
      return
    }

    couponWriteLock.busy = true
    try {
      // The campaign code is the one the customer was just promised, so a code
      // it cannot sit beside makes room for it — however deep that code was.
      const displaced = await codesDisplacedBy(existing, urlCoupon)
      for (const code of displaced)
        await activeOrderStore.removeCouponCode(code)

      try {
        await activeOrderStore.applyCouponCode(urlCoupon.code)
      }
      catch (error) {
        // Never leave the customer with neither: put back whatever we took off.
        // An expired code refuses to go back on, which is fine — it was
        // discounting nothing, which is why it was displaced in the first place.
        for (const code of displaced)
          await activeOrderStore.applyCouponCode(code).catch(() => {})
        throw error
      }
      syncLegacyActiveOrder()
    }
    catch (error) {
      // The cart is the authority: a code the order refuses (wholesale customer,
      // limit reached between validation and here) must stop being advertised.
      console.error('Failed to apply the campaign coupon', error)
      forget()
    }
    finally {
      couponWriteLock.busy = false
    }
  }, { immediate: true })
}

/**
 * Takes this server render out of every shared cache.
 *
 * The routeRules in `nuxt.config` hand `/{locale}`, `/{locale}/collections/**`
 * and `/{locale}/products/**` to the CDN for an hour, keyed on the URL — query
 * string included — and never on cookies. A render priced from the `promo.code`
 * cookie would poison that: it looks exactly like an ordinary request for the
 * same path, so the edge would hand its discount to everyone behind the same
 * object. Overwriting the header the route rule set keeps that discount inside
 * this one response — and with it the `Set-Cookie` that retires a dead code.
 *
 * A `?code=` landing whose code validated is left on the route rule's terms:
 * the code is in the cache key, so the prices with it deducted and the
 * `Set-Cookie` that adopts it are the same for everyone who follows the link.
 * What that buys is a campaign link served from the edge instead of an origin
 * render per click; what it costs is staleness — the shared copy keeps
 * advertising the code for up to the rule's hour after the campaign ends or is
 * switched off, and no purge reaches it (edge-cache-invalidation purges nothing
 * for a coupon promotion). The cart is the authority there: it refuses the
 * dead code, and `forget()` drops the advertised price.
 *
 * `private` as well as `no-store` because the two directives are read by
 * different things: no-store is the instruction to CloudFront, private repeats
 * it for any intermediary that only honours the older directive.
 */
function keepThisRenderPrivate(event: H3Event | undefined): void {
  if (!event?.node?.res || event.node.res.headersSent)
    return
  event.node.res.setHeader('Cache-Control', 'private, no-store')
}

/** Resolves on the first true reading of `predicate`, or after `timeoutMs`. */
function waitFor(predicate: () => boolean, timeoutMs: number): Promise<void> {
  if (predicate())
    return Promise.resolve()
  return new Promise<void>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const stop = watch(predicate, (met) => {
      if (met)
        finish()
    })
    function finish() {
      stop()
      if (timer)
        clearTimeout(timer)
      resolve()
    }
    timer = setTimeout(finish, timeoutMs)
  })
}
